import React, { useEffect, useState, useMemo } from "react";
import API from "../api/axios";
import {
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  Gift,
  Loader2,
  AlertCircle,
  Filter,
  Info,
} from "lucide-react";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    from: "",
    to: "",
  });

  const fetchTransactions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/wallet/transactions");
      setTransactions(res.data || []);
    } catch (err) {
      setError("Failed to load transactions. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const txDate = new Date(tx.createdAt || tx.date);
      const from = filters.from ? new Date(filters.from) : null;
      const to = filters.to ? new Date(filters.to) : null;

      if (filters.status !== "all" && tx.status !== filters.status) return false;
      if (from && txDate < from) return false;
      if (to && txDate > to) return false;

      return true;
    });
  }, [transactions, filters]);

  const getMeta = (tx) => {
    const status = (tx.status || "approved").toLowerCase();

    const typeConfig = {
      deposit: { label: "Deposit", Icon: ArrowUpRight, baseColor: "emerald" },
      withdraw: { label: "Withdrawal", Icon: ArrowDownRight, baseColor: "red" },
      roi: { label: "ROI", Icon: DollarSign, baseColor: "blue" },
      commission: { label: "Commission", Icon: Gift, baseColor: "indigo" },
    };

    const config = typeConfig[tx.type] || typeConfig.roi;

    let amountColor = "text-gray-600";
    let amountPrefix = "";
    let badgeBg = "bg-gray-100";
    let badgeText = "text-gray-700";
    let statusText = "Unknown";

    if (status === "approved") {
      amountColor = `text-${config.baseColor}-600`;
      amountPrefix = tx.type === "withdraw" ? "- " : "+ ";
      badgeBg = "bg-emerald-100";
      badgeText = "text-emerald-700";
      statusText = "Approved";
    } else if (status === "pending") {
      amountColor = "text-yellow-600";
      amountPrefix = "";
      badgeBg = "bg-yellow-100";
      badgeText = "text-yellow-700";
      statusText = "Pending";
    } else if (status === "rejected") {
      amountColor = "text-red-600";
      amountPrefix = tx.type === "withdraw" ? "- " : "";
      badgeBg = "bg-red-100";
      badgeText = "text-red-700";
      statusText = "Rejected";
    }

    return {
      ...config,
      amountColor,
      amountPrefix,
      badgeBg,
      badgeText,
      statusText,
      iconBg: status === "pending" ? "bg-yellow-100" : `bg-${config.baseColor}-100`,
      iconColor: status === "pending" ? "text-yellow-600" : `text-${config.baseColor}-600`,
    };
  };

  const renderTable = (data, title, showAdminUTR = false) => {
    const approved = data.filter(
      (tx) => (tx.status || "approved").toLowerCase() === "approved"
    );

    const totalDeposits = approved
      .filter((tx) => tx.type === "deposit")
      .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

    const totalWithdrawals = approved
      .filter((tx) => tx.type === "withdraw")
      .reduce((sum, tx) => sum + Number(tx.originalAmount || tx.amount || 0), 0);

    const totalEarnings = approved
      .filter((tx) => tx.type === "roi" || tx.type === "commission")
      .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

    const netTotal = totalDeposits + totalEarnings - totalWithdrawals;

    return (
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 flex flex-col h-full">

        {/* Title */}
        <div className="p-4 md:p-6 bg-gray-800 text-white font-bold text-center rounded-t-3xl text-lg">
          {title}
        </div>

        {/* 🔥 Mobile Scrollable Wrapper */}
        <div className="w-full overflow-x-auto overflow-y-hidden">
          <div className="min-w-[680px]"> {/* prevents table from smashing on mobile */}

            <table className="w-full text-sm md:text-base">
              <thead className="sticky top-0 bg-gray-50 border-b-2 border-gray-200 z-10">
                <tr className="text-left text-gray-700 font-semibold">
                  <th className="py-3 px-4 whitespace-nowrap">Type</th>
                  <th className="py-3 px-4 whitespace-nowrap">Amount</th>
                  <th className="py-3 px-4 whitespace-nowrap">Status</th>
                  {showAdminUTR && (
                    <th className="py-3 px-4 whitespace-nowrap">Admin UTR</th>
                  )}

                  <th className="py-3 px-4 whitespace-nowrap">Date</th>
                </tr>
              </thead>

              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-12 text-gray-500 text-base"
                    >
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  data.map((tx) => {
                    const m = getMeta(tx);
                    return (
                      <tr
                        key={tx._id || tx.id}
                        className={`border-t border-gray-100 hover:bg-gray-50 transition 
                      ${tx.status === "pending" ? "bg-yellow-50" : ""} 
                      ${tx.status === "rejected" ? "bg-red-50" : ""}`}
                      >
                        <td className="py-4 px-4 flex items-center gap-3 whitespace-nowrap">
                          <div className={`p-2.5 rounded-full ${m.iconBg}`}>
                            <m.Icon className={`w-5 h-5 ${m.iconColor}`} />
                          </div>
                          <span className="font-medium">{m.label}</span>
                        </td>

                        <td className={`py-4 px-4 font-bold whitespace-nowrap ${m.amountColor}`}>
                          {m.amountPrefix}${Number(tx.originalAmount || tx.amount).toLocaleString()}
                        </td>


                        <td className="py-4 px-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1.5 rounded-full text-xs font-bold ${m.badgeBg} ${m.badgeText}`}
                          >
                            {m.statusText}
                          </span>
                        </td>

                        {showAdminUTR && (
                          <td className="py-4 px-4 whitespace-nowrap">
                            {tx.AdminreferenceCode || "---"}
                          </td>
                        )}


                        <td className="py-4 px-4 text-gray-600 text-sm whitespace-nowrap">
                          {formatDate(tx.createdAt || tx.date)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals Section */}
        <div className="p-4 md:p-6 bg-gray-50 border-t space-y-3 text-sm md:text-base font-semibold">

          {title === "All Transactions" && (
            <>
              <div className="flex justify-between">
                <span>Deposits (Approved):</span>
                <span className="text-emerald-600">
                  ${totalDeposits.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between">
                <span>Earnings (ROI + Comm.):</span>
                <span className="text-blue-600">
                  ${totalEarnings.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between">
                <span>Withdrawals:</span>
                <span className="text-red-600">
                  -${totalWithdrawals.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between pt-3 border-t font-bold text-base md:text-lg">
                <span>Net Balance Impact:</span>
                <span
                  className={netTotal >= 0 ? "text-emerald-600" : "text-red-600"}
                >
                  ${Math.abs(netTotal).toLocaleString()}{" "}
                  {netTotal >= 0 ? "" : "(Loss)"}
                </span>
              </div>
            </>
          )}

          {title === "Deposits Only" && (
            <div className="flex justify-between">
              <span>Total Approved Deposits:</span>
              <span className="text-emerald-600">
                ${totalDeposits.toLocaleString()}
              </span>
            </div>
          )}

          {title === "Earnings Only" && (
            <div className="flex justify-between">
              <span>Total Approved Earnings:</span>
              <span className="text-blue-600">
                ${totalEarnings.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="px-4 py-3 bg-blue-50 border-t text-blue-700 text-xs md:text-sm font-medium flex items-center gap-2">
          <Info className="w-4 h-4 flex-shrink-0" />
          Pending & Rejected amounts are not included in totals
        </div>
      </div>
    );
  };


  const deposits = filteredTransactions.filter((t) => t.type === "deposit");
  const earnings = filteredTransactions.filter(
    (t) => t.type === "roi" || t.type === "commission"
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="lg:pl-6 lg:pr-6 mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
            Transaction Dashboard
          </h1>

          <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3 bg-white rounded-2xl p-4 shadow-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-600 outline-none font-medium text-sm md:text-base"
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="flex items-center gap-2 text-sm md:text-base">
              <input
                type="date"
                className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl"
                value={filters.from}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, from: e.target.value }))
                }
              />
              <span className="text-gray-500 font-medium hidden sm:inline">to</span>
              <input
                type="date"
                className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl"
                value={filters.to}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, to: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        {/* Loading / Error / Content */}
        {loading ? (
          <div className="flex flex-col items-center py-20">
            <Loader2 className="w-14 h-14 animate-spin text-gray-700" />
            <p className="mt-6 text-gray-600 text-lg">Loading transactions...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-8 rounded-3xl text-center shadow-lg">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
            <p className="text-xl font-medium">{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {renderTable(filteredTransactions, "All Transactions", true)}
            {renderTable(deposits, "Deposits Only")}
            {renderTable(earnings, "Earnings Only")}
          </div>
        )}
      </div>
    </div>
  );
}